import { AxiosError } from 'axios';
import { inject, injectable } from 'inversify';
import { BlockedRequestError, NetworkError } from '@kiplin/web-common/network';
import { ErrorDependencyTypes } from '@shared/common/error/error.dependency-types';
import type { CockpitApiErrorDataTransformerLocator } from '@shared/common/error/data/resolver/cockpit-api-error-data-transformer.resolver';
import type { AbstractCockpitError } from '@shared/common/error/domain/error/abstract-cockpit.error';
import { NetworkCockpitError } from '@shared/common/error/domain/error/network-cockpit.error';
import { UnknownError } from '@shared/common/error/domain/error/unknown.error';
import type { Composer } from 'vue-i18n';
import { TranslationDependencyTypes } from '@kiplin/web-common/translation';

@injectable()
export class CockpitApiErrorHandlerService {
  private static readonly CONNECTION_ERROR_TRANSLATION_KEY = 'common.error.connection-impossible';
  private static readonly REQUEST_ERROR_TRANSLATION_KEY = 'common.error.request-failed';

  public constructor(
    @inject(ErrorDependencyTypes.CockpitApiErrorDataTransformerLocator)
    private readonly cockpitApiErrorDataTransformerLocator: CockpitApiErrorDataTransformerLocator,
    @inject(TranslationDependencyTypes.i18n)
    private readonly i18n: Composer,
  ) {}

  public handle(error: unknown): AbstractCockpitError {
    if (error instanceof NetworkError) {
      return this.createNetworkCockpitError(error);
    }

    if (error instanceof BlockedRequestError) {
      return this.createUnknownRequestError(error);
    }

    if (!(error instanceof AxiosError)) {
      throw error;
    }

    if (error.code === AxiosError.ERR_NETWORK) {
      return this.createNetworkCockpitError(error);
    }

    const response = error.response;
    if (response === undefined) {
      return this.createUnknownRequestError(error);
    }

    const dataTransformer = this.cockpitApiErrorDataTransformerLocator.find(response.status);
    return dataTransformer.map(response.data, { status: response.status, cause: error });
  }

  private createNetworkCockpitError(cause: unknown): NetworkCockpitError {
    return new NetworkCockpitError(
      String(this.i18n.t(CockpitApiErrorHandlerService.CONNECTION_ERROR_TRANSLATION_KEY)),
      cause,
    );
  }

  private createUnknownRequestError(cause: unknown): UnknownError {
    return new UnknownError(
      String(this.i18n.t(CockpitApiErrorHandlerService.REQUEST_ERROR_TRANSLATION_KEY)),
      undefined,
      'Cockpit API request failed without a response for an unknown reason.',
      cause,
    );
  }
}
